import { api } from "./api";

export type CloudStreamStatus =
  | "creating"
  | "active"
  | "paused"
  | "stopped"
  | "error"
  | "archived";

export interface CloudRestreamLink {
  destinationId?: string | null;
  platform: string;
  uri: string;
  key?: string | null;
  restreamId?: string | null;
  active: boolean;
}

export interface CloudStream {
  id: string;
  userId: number | null;
  name: string;
  cloudStreamId: string | null;
  /** URL de ingest (RTMP/RTMPS) a la que Kero pushea el stream compuesto. */
  ingestUrl: string | null;
  rtmpStreamKey: string | null;
  srtStreamKey: string | null;
  status: CloudStreamStatus;
  playlistId: string | null;
  restreamLinks: CloudRestreamLink[];
  autoRecordEnabled: boolean;
  planId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCloudStreamInput {
  name: string;
  autoRecord?: boolean;
}

/** Un destino CLOUD que el cliente manda para que el backend cree el Live Output en Cloudflare. */
export interface CloudDestinationInput {
  id?: string;
  name: string;
  /** URL RTMP del destino (sin la key). */
  endpoint: string;
  key: string;
}

/** Resumen de salud del encoder reportado al parar (Fase 5.1). Todos opcionales. */
export interface StreamHealthInput {
  avgBitrateKbps?: number;
  droppedFrames?: number;
  bytesEgress?: number;
  renditions?: number;
}

export interface StartBroadcastInput {
  name?: string;
  autoRecord?: boolean;
  /** Destinos que el usuario marcó como "cloud" → Cloudflare Live Outputs (pago, plan). */
  cloudDestinations?: CloudDestinationInput[];
}

export interface StartBroadcastResult {
  stream: CloudStream;
  /** A dónde pushea Kero el stream compuesto (ingest de Cloudflare). */
  ingestUrl: string | null;
  rtmpStreamKey: string | null;
  srtUrl: string | null;
  /** Destinos local del repo backend (el desktop ya los tiene local → suele venir []). */
  localDestinations: Array<{
    id: string;
    name: string;
    endpoint: string;
    key: string;
    encoding: Record<string, unknown> | null;
  }>;
  /** Cantidad de Live Outputs cloud creados. */
  cloudCount: number;
}

/**
 * Cliente del backend para streams Cloud (PaaS). Reemplaza al flujo de provisioning
 * de VM: `create()` devuelve el ingest URL que Kero usa como destino RTMP.
 */
export const cloudStreamService = {
  async create(input: CreateCloudStreamInput): Promise<CloudStream> {
    const r = await api.authPost<{ data: CloudStream }>(
      "/cloud-streams",
      input,
    );
    return r.data;
  },

  /**
   * Arranca un broadcast cloud en UN paso: crea el stream Cloudflare (ingest URL) + arma los
   * Live Outputs de los destinos cloud que mandamos, y devuelve el ingest + el conteo cloud.
   * Kero pushea el stream compuesto a `ingestUrl/rtmpStreamKey` y Cloudflare fan-out a los cloud;
   * los destinos local los sigue emitiendo Kero directo.
   */
  async startBroadcast(
    input: StartBroadcastInput,
  ): Promise<StartBroadcastResult> {
    const r = await api.authPost<{ data: StartBroadcastResult }>(
      "/cloud-streams/start",
      input,
    );
    return r.data;
  },

  /**
   * Detiene el broadcast cloud (libera el cupo de concurrencia en el backend).
   * `id` = ID local del CloudStream (lo devuelve `startBroadcast` en `stream.id`).
   */
  async stopBroadcast(id: string): Promise<void> {
    await api.authPost<{ success: boolean }>(`/cloud-streams/${id}/stop`, {});
  },

  async list(): Promise<CloudStream[]> {
    const r = await api.authGet<{ data: CloudStream[] }>("/cloud-streams");
    return r.data;
  },

  async get(id: string): Promise<CloudStream> {
    const r = await api.authGet<{ data: CloudStream }>(`/cloud-streams/${id}`);
    return r.data;
  },

  /** Crea en Cloud un restream por cada destino habilitado del usuario. */
  async setupRestreams(id: string): Promise<CloudStream> {
    const r = await api.authPost<{ data: CloudStream }>(
      `/cloud-streams/${id}/restreams`,
      {},
    );
    return r.data;
  },

  /**
   * Tras parar un broadcast cloud con grabación, dispara el archivado del VOD:
   * el backend hace getRecordings → enableDownload → baja el MP4 → lo sube a R2 → crea el Vod.
   * `streamId` = ID local del CloudStream (lo devuelve `startBroadcast` en `stream.id`).
   */
  async finalizeRecording(streamId: string): Promise<{ id: string } | null> {
    const r = await api.authPost<{ data: { id: string } | null }>(
      "/recordings/finalize",
      { streamId },
    );
    return r.data;
  },

  /**
   * Reporta la salud del encoder (bitrate/fps/dropped/bytes/renditions) de un broadcast al
   * backend, que la persiste en la sesión (Fase 5.1). Punto de integración: lo llamará el
   * stop() del broadcast cuando Kero emita stats periódicas del pipeline (telemetría Rust
   * pendiente) o el path relay. No-fatal: fire-and-forget en el caller.
   * `id` = ID local del CloudStream (= sessionKey de la sesión).
   */
  async reportHealth(id: string, health: StreamHealthInput): Promise<void> {
    await api.authPost<{ success: boolean }>(
      `/cloud-streams/${id}/health`,
      health,
    );
  },

  async setRecording(id: string, enabled: boolean): Promise<CloudStream> {
    const r = await api.authPost<{ data: CloudStream }>(
      `/cloud-streams/${id}/recording`,
      { enabled },
    );
    return r.data;
  },

  async remove(id: string): Promise<void> {
    await api.authDelete<{ success: boolean }>(`/cloud-streams/${id}`);
  },
};

export default cloudStreamService;
